/**
 * EstadoGeneralCard
 * Mini cards (estilo CompactStat) que actúan como pseudo-tabs.
 * Una sección siempre activa; click cambia la activa (no colapsa).
 */
import { useMemo } from 'react';
import { ShieldCheck, ShieldAlert, TrendingUp, FileText, Send, FileSignature, Activity } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useBackofficeOffers } from '@/features/credit/hooks/useBackofficeOffers';
import { useBackofficeApplications } from '@/features/credit/hooks/useBackofficeApplications';
import type { ExpedienteResumenResponse } from '@/types/expedientes.types';

export type ExpedienteSection =
  | 'identity'
  | 'profile'
  | 'offers'
  | 'applications'
  | 'contracts'
  | 'trayectoria';

interface EstadoGeneralCardProps {
  resumen: ExpedienteResumenResponse;
  directoryId: string;
  activeSection: ExpedienteSection;
  onSelectSection: (section: ExpedienteSection) => void;
}

export function EstadoGeneralCard({ resumen, directoryId, activeSection, onSelectSection }: EstadoGeneralCardProps) {
  const identityOk = resumen.identity_verified.verified;
  const scoreValue = resumen.financial_profile?.score_total ?? null;

  const { offers } = useBackofficeOffers();
  const { applications } = useBackofficeApplications();

  const offersCount = useMemo(
    () => offers.filter((o) => o.directory_id === directoryId).length,
    [offers, directoryId],
  );
  const applicationsCount = useMemo(
    () => applications.filter((a) => a.directory_id === directoryId).length,
    [applications, directoryId],
  );

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      <NavCard
        section="identity"
        active={activeSection === 'identity'}
        onSelect={onSelectSection}
        icon={identityOk ? ShieldCheck : ShieldAlert}
        accent={identityOk ? 'green' : 'red'}
        label="Identidad"
        value={identityOk ? 'Verificada' : 'Sin verificar'}
      />
      <NavCard
        section="profile"
        active={activeSection === 'profile'}
        onSelect={onSelectSection}
        icon={TrendingUp}
        accent="blue"
        label="Score"
        value={scoreValue != null ? formatScore(scoreValue) : '—'}
      />
      <NavCard
        section="offers"
        active={activeSection === 'offers'}
        onSelect={onSelectSection}
        icon={Send}
        label="Ofertas"
        value={String(offersCount)}
      />
      <NavCard
        section="applications"
        active={activeSection === 'applications'}
        onSelect={onSelectSection}
        icon={FileSignature}
        label="Solicitudes"
        value={String(applicationsCount)}
      />
      <NavCard
        section="contracts"
        active={activeSection === 'contracts'}
        onSelect={onSelectSection}
        icon={FileText}
        label="Contratos"
        value={String(resumen.contracts_count.total)}
      />
      <NavCard
        section="trayectoria"
        active={activeSection === 'trayectoria'}
        onSelect={onSelectSection}
        icon={Activity}
        accent="blue"
        label="Trayectoria"
        value="Ver"
      />
    </div>
  );
}

type NavAccent = 'default' | 'green' | 'red' | 'blue';

const ACCENT_CLASSES: Record<NavAccent, string> = {
  default: 'bg-muted text-muted-foreground',
  green: 'bg-green-500/10 text-green-500 dark:bg-green-900/20 dark:text-green-400',
  red: 'bg-red-500/10 text-red-500 dark:bg-red-900/20 dark:text-red-400',
  blue: 'bg-blue-500/10 text-blue-500 dark:bg-blue-900/20 dark:text-blue-400',
};

function NavCard({
  section,
  active,
  onSelect,
  icon: Icon,
  accent = 'default',
  label,
  value,
}: {
  section: ExpedienteSection;
  active: boolean;
  onSelect: (s: ExpedienteSection) => void;
  icon: LucideIcon;
  accent?: NavAccent;
  label: string;
  value: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(section)}
      className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border bg-card hover:border-primary/50 hover:bg-accent/30'
      }`}
    >
      <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${ACCENT_CLASSES[accent]}`}
      >
        <Icon size={14} />
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <span className={`text-sm font-bold leading-tight tabular-nums ${active ? 'text-primary' : 'text-foreground'}`}>
          {value}
        </span>
      </div>
    </button>
  );
}

function formatScore(score: number): string {
  return score.toFixed(score % 1 === 0 ? 0 : 1);
}
